import axios from 'axios';
import { message } from 'antd';
import { PUBLIC_API_BASE_URL } from '../config/env';

const API_URL = PUBLIC_API_BASE_URL;

// 로컬 스토리지에 토큰 저장
const setToken = token => {
    localStorage.setItem('user_token', token);
};

// 로컬 스토리지에서 토큰 가져오기
const getToken = () => {
    return localStorage.getItem('user_token');
};

/** 저장된 문자열이 실제 로그인 토큰으로 쓸 수 있는지 (빈값·'null'·'undefined' 제외) */
const isUsableToken = (token = undefined) => {
    const t = token !== undefined ? token : getToken();
    if (t == null || typeof t !== 'string') return false;
    const s = t.trim();
    if (!s) return false;
    if (s === 'null' || s === 'undefined') return false;
    return true;
};

// 로컬 스토리지에서 토큰 삭제
const removeToken = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('session_id');
};

// 리프레시 토큰 저장
const setRefreshToken = token => {
    localStorage.setItem('refresh_token', token);
};

// 리프레시 토큰 가져오기
const getRefreshToken = () => {
    return localStorage.getItem('refresh_token');
};

// 세션 ID 저장
const setSessionId = sessionId => {
    localStorage.setItem('session_id', sessionId);
};

// 세션 ID 가져오기
const getSessionId = () => {
    return localStorage.getItem('session_id');
};

// 사용자 정보 저장
const setUserInfo = user => {
    localStorage.setItem('user_info', JSON.stringify(user));
};

const getCurrentUserId = () => {
    try {
        const userInfo = localStorage.getItem('user_info');
        if (!userInfo) return null;

        const parsed = JSON.parse(userInfo);
        return parsed.id || parsed.user_id;
    } catch (e) {
        console.error('user_info 파싱 오류:', e);
        return null;
    }
};

// 사용자 정보 가져오기
const getUserInfo = () => {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
};

/** 로그인 응답·DB 컬럼명 호환 (camelCase / snake_case) */
function getSubscriptionEndRaw(userInfo) {
    if (!userInfo || typeof userInfo !== 'object') return null;
    const v = userInfo.subscriptionEndDate ?? userInfo.subscription_end_date;
    if (v === undefined || v === null || String(v).trim() === '') return null;
    return v;
}

// 사용자 등급 가져오기
const getUserGrade = () => {
    const userInfo = getUserInfo();
    return userInfo ? userInfo.grade : null;
};

// 사용 기간 만료 확인
const isSubscriptionExpired = () => {
    const userInfo = getUserInfo();
    const endRaw = getSubscriptionEndRaw(userInfo);
    if (!endRaw) return true;

    const endDate = new Date(endRaw);
    const now = new Date();

    return now > endDate;
};

/**
 * 이용 종료일 기준 남은 일수. 종료일 정보가 없으면 null (만료 아님·미설정 구분용).
 * 만료된 경우 0.
 */
const getRemainingDays = () => {
    const userInfo = getUserInfo();
    const endRaw = getSubscriptionEndRaw(userInfo);
    if (!endRaw) return null;

    const endDate = new Date(endRaw);
    if (Number.isNaN(endDate.getTime())) return null;

    const now = new Date();
    if (now > endDate) return 0;

    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
};

// 세션 유효성 검증
const validateSession = async () => {
    try {
        const sessionId = getSessionId();
        if (!sessionId) return false;

        const response = await authAxios.get(`${API_URL}/auth/validate-session`);
        return response.data;
    } catch (error) {
        console.error('세션 검증 오류:', error);
        return false;
    }
};

let sessionMonitorIntervalId = null;

const clearSessionMonitoring = () => {
    if (sessionMonitorIntervalId != null) {
        clearInterval(sessionMonitorIntervalId);
        sessionMonitorIntervalId = null;
    }
};

const notifySessionInvalid = async isValid => {
    if (isValid && typeof isValid === 'object') {
        if (isValid.reason === 'SUBSCRIPTION_EXPIRED') {
            message.info(
                '프리미엄 이용 기간이 만료되어 일반 등급으로 변경되었습니다. 다시 로그인해주세요.'
            );
        } else if (isValid.reason === 'DUPLICATE_LOGIN') {
            message.warning('다른 기기에서 로그인이 감지되었습니다. 현재 세션을 종료합니다.');
        } else if (isValid.message) {
            message.error(isValid.message);
        } else {
            message.error('세션이 만료되었습니다. 다시 로그인해주세요.');
        }
    } else {
        message.error('세션이 만료되었습니다. 다시 로그인해주세요.');
    }
    await logout({ redirect: false });
    if (!/\/login\/?$/i.test(window.location.pathname || '')) {
        window.location.replace('/login');
    }
};

/**
 * 메인 레이아웃(보호된 페이지) 진입 시 1회: 서버 세션·구독·중복 로그인 검증.
 * 로그인 화면에서는 호출하지 않음.
 */
const ensureSessionValidatedOnAppEntry = async () => {
    if (!isUsableToken() || !getSessionId()) {
        return;
    }
    const isValid = await validateSession();
    const sessionOk = isValid && typeof isValid === 'object' && isValid.valid === true;
    if (!sessionOk) {
        await notifySessionInvalid(isValid);
        return;
    }
    setupSessionMonitoring();
};

// 중복 로그인 이벤트 리스너 설정
const setupSessionMonitoring = () => {
    clearSessionMonitoring();
    sessionMonitorIntervalId = setInterval(async () => {
        const isValid = await validateSession();
        const sessionOk = isValid && typeof isValid === 'object' && isValid.valid === true;
        if (!sessionOk) {
            clearSessionMonitoring();
            await notifySessionInvalid(isValid);
        }
    }, 30000);

    window.addEventListener(
        'beforeunload',
        () => {
            clearSessionMonitoring();
        },
        { once: true }
    );
};

// 로그인 요청
const login = async (userId, password) => {
    try {
        // 디바이스/브라우저 정보 수집
        const deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timestamp: new Date().toISOString(),
        };

        const response = await axios.post(
            `${API_URL}/auth/login`,
            {
                userId,
                password,
                deviceInfo, // 디바이스 정보 포함
            },
            { timeout: 20000 }
        );

        if (response.data.token) {
            setToken(response.data.token);
            setRefreshToken(response.data.refreshToken);
            setUserInfo(response.data.user);

            // 로그인 성공 후 리턴 받은 token 등과 함께 메뉴 호출
            //const menuRes = await AuthService.authAxios.get('/auth/menu/user');

            localStorage.setItem('tsad_ranking_naverMenu', JSON.stringify(response.data.naverMenu));
            localStorage.setItem('tsad_ranking_toolMenu', JSON.stringify(response.data.toolMenu));
            localStorage.setItem('tsad_ranking_guideMenu', JSON.stringify(response.data.guideMenu));

            // 세션 ID 저장
            if (response.data.sessionId) {
                setSessionId(response.data.sessionId);
                setupSessionMonitoring();
            }

            return response.data;
        }
        return null;
    } catch (error) {
        if (error?.code === 'ECONNABORTED' || String(error?.message || '').includes('timeout')) {
            throw { message: '연결 시간이 초과되었습니다. 네트워크를 확인 후 다시 시도해주세요.' };
        }
        throw error.response?.data || error || { message: '로그인 중 오류가 발생했습니다.' };
    }
};

// 카카오 OAuth 코드로 로그인
const loginWithKakao = async (code, redirectUri) => {
    const response = await axios.post(`${API_URL}/auth/kakao/token`, { code, redirectUri });
    if (response.data.token) {
        setToken(response.data.token);
        setRefreshToken(response.data.refreshToken);
        setUserInfo(response.data.user);
        localStorage.setItem('tsad_ranking_naverMenu', JSON.stringify(response.data.naverMenu));
        localStorage.setItem('tsad_ranking_toolMenu', JSON.stringify(response.data.toolMenu));
        localStorage.setItem('tsad_ranking_guideMenu', JSON.stringify(response.data.guideMenu));
        if (response.data.sessionId) {
            setSessionId(response.data.sessionId);
            setupSessionMonitoring();
        }
        return response.data;
    }
    return null;
};

// 리프레시 토큰으로 새 액세스 토큰 요청
const refreshAccessToken = async () => {
    try {
        const refreshToken = getRefreshToken();
        const sessionId = getSessionId();

        if (!refreshToken) throw new Error('리프레시 토큰이 없습니다');

        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
            refreshToken,
            sessionId,
        });

        if (response.data.token) {
            const newToken = response.data.token;

            // ✅ 토큰 저장
            setToken(newToken);

            // ✅ axios 전역 헤더 갱신 (가장 중요!)
            authAxios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

            // ✅ 세션 / 유저 정보 갱신
            if (response.data.sessionId) setSessionId(response.data.sessionId);
            if (response.data.user) setUserInfo(response.data.user);

            console.log('🔄 새 토큰 갱신 완료');
            return newToken;
        }

        return null;
    } catch (error) {
        console.error('토큰 갱신 오류:', error);
        throw error;
    }
};

// 로그아웃
// options.redirect: true(기본) = 페이지 전체 리로드 후 /login 이동 (인터셉터 등에서 사용)
// options.redirect: false = 저장소만 삭제, 리다이렉트 없음 (UI 로그아웃 버튼에서 navigate 사용 시)
const logout = async (options = {}) => {
    const { redirect = true } = options;
    const refreshToken = getRefreshToken();
    const sessionId = getSessionId();

    // 먼저 동기적으로 저장소 삭제 (navigate 후 로그인 페이지에서 토큰 미존재 확인)
    removeToken();
    localStorage.removeItem('user_info');
    localStorage.removeItem('tsad_ranking_naverMenu');
    localStorage.removeItem('tsad_ranking_toolMenu');
    localStorage.removeItem('tsad_ranking_guideMenu');

    try {
        if (refreshToken && sessionId) {
            await axios.post(`${API_URL}/auth/logout`, {
                refreshToken,
                sessionId,
            });
        }
    } catch (error) {
        console.error('로그아웃 오류:', error);
    }

    if (redirect && !/\/login\/?$/i.test(window.location.pathname || '')) {
        window.location.replace('/login');
    }
};

// 등급별 권한 체크
const checkGradePermission = requiredGrade => {
    const userInfo = getUserInfo();
    if (!userInfo) return false;

    // 등급 우선순위 정의 (높은 숫자가 더 높은 권한)
    const gradePriority = {
        user: 1,
        premium: 2,
        manager: 3,
        admin: 4,
    };

    const userGrade = userInfo.grade || 'user';
    const userPriority = gradePriority[userGrade] || 0;
    const requiredPriority = gradePriority[requiredGrade] || 0;

    return userPriority >= requiredPriority;
};

// 사용자 등급 업데이트 (관리자용)
const updateUserGrade = async (userId, newGrade) => {
    try {
        const response = await authAxios.put(`${API_URL}/admin/users/${userId}/grade`, {
            grade: newGrade,
        });
        return response.data;
    } catch (error) {
        console.error('등급 업데이트 오류:', error);
        throw error.response?.data || { message: '등급 업데이트 중 오류가 발생했습니다.' };
    }
};

// 사용 기간 업데이트 (관리자용)
const updateUserSubscription = async (userId, daysToAdd, reason = '') => {
    try {
        const response = await authAxios.put(`${API_URL}/admin/users/${userId}/subscription`, {
            daysToAdd,
            reason,
        });
        return response.data;
    } catch (error) {
        console.error('사용 기간 업데이트 오류:', error);
        throw error.response?.data || { message: '사용 기간 업데이트 중 오류가 발생했습니다.' };
    }
};

// 모든 사용자 목록 가져오기 (관리자용)
const getAllUsers = async () => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/users`);
        return response.data;
    } catch (error) {
        console.error('사용자 목록 로드 오류:', error);
        throw error.response?.data || { message: '사용자 목록을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 로그인 기록 조회 (관리자용)
const getLoginLogs = async (filters = {}) => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/login-logs`, { params: filters });
        return response.data;
    } catch (error) {
        console.error('로그인 기록 로드 오류:', error);
        throw error.response?.data || { message: '로그인 기록을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 현재 활성 세션 목록 조회 (관리자용)
const getActiveSessions = async () => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/active-sessions`);
        return response.data;
    } catch (error) {
        console.error('활성 세션 로드 오류:', error);
        throw error.response?.data || { message: '활성 세션을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 특정 세션 강제 종료 (관리자용)
const terminateSession = async sessionId => {
    try {
        const response = await authAxios.delete(`${API_URL}/admin/sessions/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('세션 종료 오류:', error);
        throw error.response?.data || { message: '세션 종료 중 오류가 발생했습니다.' };
    }
};

// 사용 기간 이력 조회 (관리자용)
const getSubscriptionHistory = async userId => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/subscription-history`, {
            params: { userId },
        });
        return response.data;
    } catch (error) {
        console.error('사용 기간 이력 로드 오류:', error);
        throw (
            error.response?.data || { message: '사용 기간 이력을 불러오는 중 오류가 발생했습니다.' }
        );
    }
};

// 인증이 필요한 API 요청에 사용할 Axios 인스턴스
const authAxios = axios.create({
    baseURL: API_URL,
});

// 요청 인터셉터 - 토큰을 헤더에 추가
authAxios.interceptors.request.use(
    config => {
        const token = getToken();
        const sessionId = getSessionId();

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // 세션 ID가 있으면 헤더에 추가
        if (sessionId) {
            config.headers['X-Session-ID'] = sessionId;
        }

        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터 - 토큰 만료 시 리프레시 토큰으로 새 토큰 요청
authAxios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // 🔒 액세스 토큰 만료 시 처리
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // ✅ 전역 토큰 갱신
                    authAxios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

                    // ✅ 현재 요청에 새 토큰 반영
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

                    // ✅ 세션 ID 갱신
                    const sessionId = getSessionId();
                    if (sessionId) {
                        originalRequest.headers['X-Session-ID'] = sessionId;
                    }

                    console.log('🔄 토큰 갱신 성공 — 요청 재시도 중...');
                    return axios({
                        ...originalRequest,
                        baseURL: API_URL,
                        headers: {
                            ...originalRequest.headers,
                            Authorization: `Bearer ${newToken}`,
                            'X-Session-ID': getSessionId(),
                        },
                    });
                }
                await logout({ redirect: false });
                if (!/\/login\/?$/i.test(window.location.pathname || '')) {
                    window.location.replace('/login');
                }
                return Promise.reject(new Error('AUTH_REFRESH_EMPTY'));
            } catch (refreshError) {
                console.error('❌ 토큰 갱신 실패:', refreshError);
                await logout({ redirect: false });
                if (!/\/login\/?$/i.test(window.location.pathname || '')) {
                    window.location.replace('/login');
                }
                return Promise.reject(refreshError);
            }
        }

        // ⚠️ 중복 로그인 (409)
        if (error.response?.status === 409) {
            message.warning('다른 기기에서 로그인이 감지되었습니다. 현재 세션을 종료합니다.');
            await logout({ redirect: false });
            if (!/\/login\/?$/i.test(window.location.pathname || '')) {
                window.location.replace('/login');
            }
        }

        // ⚠️ 사용 기간 만료 (402)
        if (error.response?.status === 402) {
            message.error('이용 기간이 만료되었습니다. 관리자에게 문의하세요.');
            await logout({ redirect: false });
            if (!/\/login\/?$/i.test(window.location.pathname || '')) {
                window.location.replace('/login');
            }
        }

        return Promise.reject(error);
    }
);

const AuthService = {
    login,
    loginWithKakao,
    logout,
    validateSession,
    ensureSessionValidatedOnAppEntry,
    getToken,
    isUsableToken,
    getRefreshToken,
    getSessionId,
    refreshAccessToken,
    getUserInfo,
    getUserGrade,
    isSubscriptionExpired,
    getRemainingDays,
    checkGradePermission,
    updateUserGrade,
    updateUserSubscription,
    getAllUsers,
    getLoginLogs,
    getActiveSessions,
    terminateSession,
    getSubscriptionHistory,
    getCurrentUserId,
    authAxios,
};

export default AuthService;
